import type { PassCredits, PassPeriod } from '@/types';
import type { PillTone } from '@/shared/ui/Pill';
import {
  getCalendarDateKey,
  getTimeUntilDay,
  getTodayForInput,
} from './calendarDate';

export type PassTimeStatus = 'future' | 'active' | 'expired';

export const EXPIRY_WARNING_DAYS = 7;

export interface PassMeta {
  timeStatus: PassTimeStatus;
  remainingCredits: number | null;
  hasCredits: boolean;
  isActive: boolean;
  isFuture: boolean;
  isExpired: boolean;
  isOutOfCredits: boolean;
  daysUntilExpiry: number | null;
  isEndingSoon: boolean;
}

type PassWithCredits = PassPeriod & { credits?: PassCredits | null };

export const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const words = trimmed.split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

export const getPassMeta = (pass?: PassWithCredits | null): PassMeta | null => {
  if (!pass) return null;

  const timeStatus = getPassTimeStatus(pass);

  const remainingCredits = pass.credits?.remainingCredits ?? null;
  const isOutOfCredits = remainingCredits === 0;
  const hasCredits = remainingCredits === null || remainingCredits > 0;

  const isActive = timeStatus === 'active' && hasCredits;
  const isFuture = timeStatus === 'future';
  const isExpired = timeStatus === 'expired';

  const daysUntilExpiry =
    isActive && pass.endsAt
      ? (getTimeUntilDay(pass.endsAt)?.calendarDayOffset ?? null)
      : null;

  const isEndingSoon =
    daysUntilExpiry !== null && daysUntilExpiry <= EXPIRY_WARNING_DAYS;

  return {
    timeStatus,
    remainingCredits,
    hasCredits,
    isActive,
    isFuture,
    isExpired,
    isOutOfCredits,
    daysUntilExpiry,
    isEndingSoon,
  };
};

export const getPassTimeStatus = (pass: PassPeriod): PassTimeStatus => {
  const today = getTodayForInput();
  const startDate = getCalendarDateKey(pass.startsAt);

  if (today < startDate) {
    return 'future';
  }

  if (pass.endsAt === null) {
    return 'active';
  }

  const endDate = getCalendarDateKey(pass.endsAt);
  if (today <= endDate) {
    return 'active';
  }

  return 'expired';
};

export const getPassTone = (meta: PassMeta | null): PillTone => {
  if (!meta) return 'neutral';
  if (meta.isExpired || meta.isOutOfCredits) return 'destructive';
  if (meta.isEndingSoon) return 'warning';
  if (meta.isActive) return 'success';
  return 'neutral';
};
